import { useEffect, useMemo, useState } from 'react';
import { PERSPECTIVE_ORDER, STATUS_PRIORITY } from '../config/kpi';
import { normalizeSearchText } from '../lib/text';
import type {
  DashboardFilters,
  KpiStatus,
  LoadedBscData,
  SnapshotRow,
} from '../types/bsc';
import { AppHeader } from './AppHeader';
import { FilterDrawer } from './FilterDrawer';
import { PerspectiveAccordion } from './PerspectiveAccordion';
import { StatusChart } from './StatusChart';

interface DashboardProps {
  data: LoadedBscData;
  userLabel: string;
  refreshing: boolean;
  onReload: () => void;
  onSignOut?: () => void;
}

const STATUS_DISPLAY_ORDER: KpiStatus[] = [
  'Eccellente',
  'In target',
  'Fuori target',
  'Sotto target',
  'Critico',
  'Manca dato',
  'Da definire',
  'No target',
];

function getAvailableStatuses(snapshot: SnapshotRow[]): KpiStatus[] {
  const found = new Set(snapshot.map((row) => row.Status));
  return STATUS_DISPLAY_ORDER.filter((status) => found.has(status));
}

function filterRows(snapshot: SnapshotRow[], filters: DashboardFilters): SnapshotRow[] {
  const query = normalizeSearchText(filters.query);
  return snapshot.filter((row) => {
    if (!filters.perspectives.includes(row.Prospettiva_BSC)) return false;
    if (!filters.statuses.includes(row.Status)) return false;
    if (!query) return true;

    const haystack = normalizeSearchText(
      [row.Codice, row.Nome, row.Descrizione_KPI, row.Responsabile].join(' '),
    );
    return haystack.includes(query);
  });
}

function latestPeriod(snapshot: SnapshotRow[]): string {
  const dates = snapshot
    .map((row) => row.period)
    .filter((value): value is string => Boolean(value))
    .sort();
  if (dates.length === 0) return 'n.d.';

  const date = new Date(`${dates[dates.length - 1]}T12:00:00Z`);
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export function Dashboard({
  data,
  userLabel,
  refreshing,
  onReload,
  onSignOut,
}: DashboardProps) {
  const availableStatuses = useMemo(
    () => getAvailableStatuses(data.snapshot),
    [data.snapshot],
  );
  const [filters, setFilters] = useState<DashboardFilters>(() => ({
    perspectives: [...PERSPECTIVE_ORDER],
    statuses: [...availableStatuses],
    query: '',
  }));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [showBreakdownTable, setShowBreakdownTable] = useState(false);

  useEffect(() => {
    setFilters((current) => {
      const retained = current.statuses.filter((status) => availableStatuses.includes(status));
      const newlyAvailable = availableStatuses.filter((status) => !current.statuses.includes(status));
      return { ...current, statuses: [...retained, ...newlyAvailable] };
    });
  }, [availableStatuses]);

  const filtered = useMemo(
    () => filterRows(data.snapshot, filters),
    [data.snapshot, filters],
  );

  const criticalCount = useMemo(
    () =>
      data.snapshot.filter(
        (row) => STATUS_PRIORITY[row.Status] >= STATUS_PRIORITY['Fuori target'],
      ).length,
    [data.snapshot],
  );

  return (
    <div className="app-shell">
      <AppHeader
        userLabel={userLabel}
        loadedAt={data.loadedAt}
        refreshing={refreshing}
        onOpenControls={() => setDrawerOpen(true)}
        onSignOut={onSignOut}
      />

      <FilterDrawer
        open={drawerOpen}
        filters={filters}
        availableStatuses={availableStatuses}
        showMoreDetails={showMoreDetails}
        showBreakdownTable={showBreakdownTable}
        refreshing={refreshing}
        onClose={() => setDrawerOpen(false)}
        onFiltersChange={setFilters}
        onShowMoreDetailsChange={setShowMoreDetails}
        onShowBreakdownTableChange={setShowBreakdownTable}
        onReload={onReload}
      />

      <main className="dashboard-main">
        <section className="hero-section">
          <div>
            <span className="eyebrow">ELVI · controllo direzionale</span>
            <h1>Balanced Scorecard</h1>
            <p>
              Vista sintetica e storica dei KPI. I dati sono letti direttamente dai file Excel
              autorizzati su Amazon S3.
            </p>
          </div>
          <div className="hero-pill" title="Architettura senza server applicativo sempre acceso">
            <span className="hero-pill-dot" />
            Frontend statico
          </div>
        </section>

        <section className="summary-grid" aria-label="Indicatori riepilogativi">
          <article className="summary-card">
            <span>KPI censiti</span>
            <strong>{data.snapshot.length}</strong>
            <small>{PERSPECTIVE_ORDER.length} prospettive BSC</small>
          </article>
          <article className="summary-card summary-card--critical">
            <span>Da presidiare</span>
            <strong>{criticalCount}</strong>
            <small>Fuori target o critici</small>
          </article>
          <article className="summary-card">
            <span>Ultimo periodo</span>
            <strong className="summary-date">{latestPeriod(data.snapshot)}</strong>
            <small>Aggiornamento massimo rilevato</small>
          </article>
          <article className="summary-card">
            <span>Vista corrente</span>
            <strong>{filtered.length}</strong>
            <small>KPI dopo i filtri</small>
          </article>
        </section>

        <StatusChart snapshot={filtered} />

        <section className="dashboard-section" aria-labelledby="dashboard-section-title">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Analisi</span>
              <h2 id="dashboard-section-title">Cruscotto</h2>
            </div>
            <div className="active-filter-summary">
              {filters.query ? <span>Ricerca: “{filters.query}”</span> : null}
              <span>{filtered.length} KPI</span>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="empty-results">
              <h3>Nessun KPI corrisponde ai filtri</h3>
              <p>Aprire i controlli e ampliare prospettive, stati o ricerca.</p>
              <button
                type="button"
                className="button button--primary"
                onClick={() =>
                  setFilters({
                    perspectives: [...PERSPECTIVE_ORDER],
                    statuses: [...availableStatuses],
                    query: '',
                  })
                }
              >
                Ripristina la vista
              </button>
            </div>
          ) : (
            <div className="perspective-list">
              {PERSPECTIVE_ORDER.map((perspective) => {
                const rows = filtered.filter(
                  (row) => row.Prospettiva_BSC === perspective,
                );
                return rows.length > 0 ? (
                  <PerspectiveAccordion
                    key={perspective}
                    perspective={perspective}
                    rows={rows}
                    bundle={data.bundle}
                    showMoreDetails={showMoreDetails}
                    showBreakdownTable={showBreakdownTable}
                  />
                ) : null;
              })}
            </div>
          )}
        </section>
      </main>

      <footer className="app-footer">
        <span>Balanced Scorecard ELVI</span>
        <span>Excel su S3 · Cognito · React</span>
      </footer>
    </div>
  );
}
